// StatusComponent.tsx - FIXED VERSION
import React, { useEffect, useState } from "react";
import styles from "./StatusComponent.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Button from "../UI/Buttons/Button/Button";
import { RootState } from "../../store";
import {
  logoutAction,
  setCNCActiveStatus,
  fetchMarketplaceInfoDetailed,
} from "../../store/auth-actions";
import {
  authActions,
  selectHasSupportedPharmacistCapability,
  selectIsConsultOnlySession,
  selectIsDualRetailPharmacist,
} from "../../store/auth-slice";
import { selectHasUnfinishedConsultDraft } from "../../store/consultOrder-slice";
import axios, { baseUrl } from "../../axios/axios";
import StoreInfo from "./PharmacyDetails/PharmacyDetails";
import PromoCategories from "./PromoCategories/PromoCategories";
import PharmacyImagesAndStatus from "./PharmacyImagesAndStatus/PharmacyImagesAndStatus";
import { useGlobalContext } from "../../store/context/GlobalContext";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import { getTokenUserId } from "../../utils/tokenUtils";
import {
  clearTakesOrdersOnLogout,
  pausePharmacistOnLogout,
  runBoundedLogoutSideEffects,
} from "../../services/sessionSideEffects";

type Props = {
  isAdmin?: boolean;
  isPharmacist?: boolean;
  isDriver?: boolean;
  isMarketplaceAdmin?: boolean;
};

const StatusComponent = ({
  isAdmin,
  isPharmacist,
  isDriver,
  isMarketplaceAdmin,
}: Props) => {
  const dispatch = useDispatch();
  const { clearContext } = useGlobalContext();

  // Use Redux state instead of localStorage
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo,
  );

  // ITAM-2622 (3.6a/3.7): the logout control is a sibling of the consult
  // provider subtree and can reach neither the draft nor the hook's phase, so
  // both gates read Redux signals published for exactly this purpose.
  const activeConsult = useSelector(
    (state: RootState) => state.videoConsult.activeConsult,
  );
  const hasUnfinishedDraft = useSelector(selectHasUnfinishedConsultDraft);
  const hasPharmacistCapability = useSelector(
    selectHasSupportedPharmacistCapability,
  );
  const isConsultOnly = useSelector(selectIsConsultOnlySession);
  const isDualRetail = useSelector(selectIsDualRetailPharmacist);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const [activeButtonTitle, setActiveButtonTitle] = useState("Status: aktivna");
  const [marketplaceInfoDetailed, setMarketplaceInfoDetailed] = useState<any>(
    {},
  );
  const [marketplaceWorkingHours, setMarketplaceWorkingHours] = useState<any>(
    {},
  );

  // Only fetch when marketplace info is available from Redux
  useEffect(() => {
    if (
      marketplaceInfo?.branchMarketplaceId &&
      marketplaceInfo?.marketplaceId
    ) {
      // DUAL MODE: Fetch for both local state AND Redux
      // Local state - for backward compatibility with existing components
      fetchMarketPlaceDetails(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
      ).then((res) => setMarketplaceInfoDetailed(res.data));

      // Redux state - for new PharmacistInfo component in header
      dispatch<any>(
        fetchMarketplaceInfoDetailed(
          marketplaceInfo.branchMarketplaceId,
          marketplaceInfo.marketplaceId,
        ),
      );

      fetchMarketPlaceWorkingHours(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
      ).then((res) => setMarketplaceWorkingHours(res.data));
    }
  }, [marketplaceInfo, dispatch]); // ✅ Depend on Redux state, not localStorage

  useEffect(() => {
    if (marketplaceInfo && marketplaceInfo?.takesOrders === 1) {
      setActiveButtonTitle("Status: aktivna");
    } else {
      setActiveButtonTitle("Status: neaktivna");
    }
  }, [marketplaceInfo]);

  const logout = () => {
    clearContext(); // Clear global state
    dispatch<any>(
      logoutAction(
        marketplaceInfo?.branchMarketplaceId ?? null,
        marketplaceInfo?.marketplaceId ?? null,
        0,
      ),
    );
  };

  const logoutAdmin = () => {
    clearContext(); // Clear global state
    dispatch(authActions.logout());
  };

  // --- ITAM-2622 (3.7): the third logout path ------------------------------
  //
  // Consult-only: best-effort pause, then clear the session. Dual-role: the
  // takes_orders-off PUT AND the pause, neither blocking the other. The retail
  // PUT keys off the RETAIL marketplace ids — never the pharmacist profile
  // pair, which decision 14 makes an independent association.
  //
  // The session is cleared IMMEDIATELY rather than after awaiting, and that is
  // deliberate. `HeaderComponent` runs a 2-minute keep-alive that PUTs
  // `takesOrders=1`, whose only teardown is `marketplaceInfo` changing. Today's
  // `logoutAction` disarms it as a SIDE EFFECT of dispatching
  // `setActiveStatus(0)`; the bare service calls dispatch nothing, so awaiting
  // them would hold that interval armed for up to the full timeout on exactly
  // the dual-role sessions this path targets — and a tick landing after the
  // `takesOrders=0` PUT would leave the pharmacy active after logout.
  // Dispatching first shrinks that window to the React commit that unmounts the
  // header. The contract is best effort, not guaranteed delivery (decision 26);
  // in exchange the session guarantee is timing-independent.
  const performPharmacistLogout = () => {
    // Captured BEFORE anything clears localStorage: `authActions.logout()`
    // calls clearLocalStorage(), and the bare instance has no request
    // interceptor to re-add the header.
    const token = localStorage.getItem("token") ?? "";
    const userId = token ? getTokenUserId(token) : null;
    const bmpId = marketplaceInfo?.branchMarketplaceId ?? null;
    const mpId = marketplaceInfo?.marketplaceId ?? null;

    const tasks: Array<(signal: AbortSignal) => Promise<unknown>> = [];
    if (userId != null) {
      tasks.push((signal) => pausePharmacistOnLogout(userId, token, signal));
    }
    if (isDualRetail && bmpId != null && mpId != null) {
      tasks.push((signal) =>
        clearTakesOrdersOnLogout(bmpId, mpId, token, signal),
      );
    }

    // Fire and observe, but never gate the session on it. The requests are
    // created synchronously here, before the dispatch below clears the token.
    void runBoundedLogoutSideEffects(tasks).catch(() => {
      /* best-effort */
    });

    clearContext();
    dispatch(authActions.logout());
  };

  const requestPharmacistLogout = () => {
    if (activeConsult) {
      return; // the control is disabled; this is belt and braces
    }
    if (hasUnfinishedDraft) {
      // Decision 28 (final wording): confirm whenever a user-initiated logout
      // would discard an unfinished consult order or RFZO draft — grace
      // detection plays no part in the gate. It only ever asks.
      setConfirmingDiscard(true);
      return;
    }
    performPharmacistLogout();
  };

  // A dual-role session logs out through the pharmacist path (both side
  // effects); a retail-only session keeps the existing behavior byte for byte,
  // including the pre-existing hazard that a failed PUT leaves it logged in —
  // repairing that is a separate decision, not ITAM-2622's.
  const retailLogout = hasPharmacistCapability
    ? requestPharmacistLogout
    : logout;

  const statusToggle = () => {
    // Add guard for marketplace info
    if (
      !marketplaceInfo?.branchMarketplaceId ||
      !marketplaceInfo?.marketplaceId
    ) {
      console.warn("Cannot toggle status: marketplace info not available");
      return;
    }

    dispatch<any>(
      setCNCActiveStatus(
        marketplaceInfo.branchMarketplaceId,
        marketplaceInfo.marketplaceId,
        marketplaceInfo.takesOrders === 1 ? 0 : 1,
      ),
    );
  };

  // ITAM-2622: the pharmacist logout gates (decisions 27/28). The dialog is
  // rendered by every branch below that can log out.
  const logoutBlockedByConsult = hasPharmacistCapability && activeConsult != null;
  const discardDialog = (
    <AccessibleDialog
      isOpen={confirmingDiscard}
      onRequestClose={() => setConfirmingDiscard(false)}
      titleId="pharmacist-logout-discard-title"
      maxWidth={480}
    >
      <div className={styles.DiscardDialog}>
        <h3
          id="pharmacist-logout-discard-title"
          className={styles.DiscardTitle}
        >
          Nezavršena rezervacija
        </h3>
        <p>
          Imate nezavršenu rezervaciju/terapiju iz konsultacije. Odjavom će biti
          odbačena.
        </p>
        <div className={styles.DiscardActions}>
          <Button
            buttonType="white"
            onClick={() => setConfirmingDiscard(false)}
          >
            Odustani
          </Button>
          <Button
            buttonType="red"
            onClick={() => {
              setConfirmingDiscard(false);
              performPharmacistLogout();
            }}
          >
            Odjavi se
          </Button>
        </div>
      </div>
    </AccessibleDialog>
  );

  // ITAM-2622: the consult-only pharmacist has no retail pharmacy at all, so it
  // must be handled BEFORE the marketplace-info guard below — otherwise this
  // session would sit forever on "Loading marketplace info...".
  if (isConsultOnly) {
    return (
      <div className={styles.StatusComponent}>
        <div className={styles.Content}>
          <span className={styles.Title}>Farmaceutski savetnik</span>
          <div className={styles.Buttons}>
            <Button
              buttonType="blue"
              disabled={logoutBlockedByConsult}
              onClick={requestPharmacistLogout}
            >
              Odjavi se
            </Button>
          </div>
          {logoutBlockedByConsult && (
            <span className={styles.LogoutBlocked} role="note">
              Odjava nije moguća tokom konsultacije.
            </span>
          )}
        </div>
        {discardDialog}
      </div>
    );
  }

  // Add loading state while marketplace info loads
  if (
    !marketplaceInfo &&
    !isAdmin &&
    !isPharmacist &&
    !isDriver &&
    !isMarketplaceAdmin
  ) {
    return (
      <div className={styles.StatusComponent}>
        <div className={styles.Content}>
          <span>Loading marketplace info...</span>
        </div>
      </div>
    );
  }

  // Rest of component remains the same...
  if (!isAdmin && !isPharmacist && !isDriver && !isMarketplaceAdmin) {
    return (
      <div className={styles.StatusComponent}>
        <div className={styles.statusComponentStore}>
          <div className={styles.columns}>
            <PharmacyImagesAndStatus
              marketplaceInfo={marketplaceInfo}
              marketplaceInfoDetailed={marketplaceInfoDetailed}
              statusToggle={statusToggle}
              activeButtonTitle={activeButtonTitle}
              logout={retailLogout}
              logoutDisabled={logoutBlockedByConsult}
              logoutBlockedMessage={
                logoutBlockedByConsult
                  ? "Odjava nije moguća tokom konsultacije."
                  : undefined
              }
              setMarketplaceInfoDetailed={setMarketplaceInfoDetailed}
            />
            {discardDialog}
          </div>
          <div className={styles.columns}>
            <StoreInfo
              marketplaceInfoDetailed={marketplaceInfoDetailed}
              marketplaceWorkingHours={marketplaceWorkingHours}
            />
          </div>
          <div className={styles.columns}>
            <PromoCategories />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.StatusComponent}>
      {!isAdmin && !isPharmacist && !isDriver && !isMarketplaceAdmin ? (
        <div className={styles.Content}>
          <span className={styles.Title}>Status apoteke</span>
          <span className={styles.PharmacyName}>
            {marketplaceInfo && marketplaceInfo.name}
          </span>
          <div className={styles.Buttons}>
            <Button
              buttonType={marketplaceInfo?.takesOrders === 1 ? "blue" : "red"}
              onClick={statusToggle}
            >
              {activeButtonTitle}
            </Button>
            <Button buttonType="blue" onClick={logout}>
              Odjavi se
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.Content}>
          {isAdmin || isDriver ? (
            <span className={styles.Title}>Admin</span>
          ) : isMarketplaceAdmin ? (
            <span className={styles.Title}>Administrator ustanove</span>
          ) : (
            <span className={styles.Title}>Farmaceut</span>
          )}
          <div className={styles.Buttons}>
            <Button buttonType="blue" onClick={logoutAdmin}>
              Odjavi se
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusComponent;

// Add marketplace info guards to utility functions
export const fetchMarketPlaceDetails = (branchId: any, marketplaceId: any) => {
  if (!branchId || !marketplaceId) {
    console.warn("Cannot fetch marketplace details: missing IDs");
    return Promise.reject("Missing marketplace IDs");
  }
  return axios.get(baseUrl + `/marketplace/${marketplaceId}/${branchId}`, {});
};

export const fetchMarketPlaceWorkingHours = (
  branchId: any,
  marketplaceId: any,
) => {
  if (!branchId || !marketplaceId) {
    console.warn("Cannot fetch working hours: missing IDs");
    return Promise.reject("Missing marketplace IDs");
  }
  return axios.get(
    baseUrl + `/marketplace/opening_hours/${marketplaceId}/${branchId}`,
    {},
  );
};
